import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';

import styled from 'styled-components';

// constants
import routes from '../../../constants/routes';

// context
import { EditorContext } from '../../../context/EditorContext';

// hooks
import useAudiencesDetailQuery from '../../../hooks/audiences/useAudiencesDetailQuery';
import formulaKeys from '../../../hooks/formulas/_queryKeys';
import useFormulaAudienceMutation from '../../../hooks/formulas/useFormulaAudienceMutation';
import useFormulaCompoundTriggerMutation from '../../../hooks/formulas/useFormulaCompoundTriggerMutation';
import useFormulaCreateMutation from '../../../hooks/formulas/useFormulaCreateMutation';
import useFormulaTagsMutation from '../../../hooks/formulas/useFormulaTagsMutation';

// models
import { ALL_USERS_AUDIENCE_ID } from '../../../models/audience.model';
import {
    CMSDisplayNames,
    CMSFilterCreateModel,
    CMSFilterType,
    DEFAULT_CMS_TYPE_OPTION,
} from '../../../models/cms-placeholder.model';
import {
    FormulaProperty,
    FormulaSettingsInitialValueProps,
    FormulaUpdateModel,
} from '../../../models/formula.model';

import { LocationState } from '../../../types/location-state';

// components
import AudienceSelect from '../../../views/audiences/AudienceSelect';

import CompoundTriggerInput from './CompoundTrigger/CompoundTriggerInput';
import TagsInput from './FormulaTags/TagsInput';
import { getTagsInitialValues } from './FormulaTags/helper';

interface FormulaSettingsSelectProps {
    filterKey: FormulaProperty | null;
    isDisabled?: boolean;
}

const FormulaSettingsSelect: FC<
    React.PropsWithChildren<FormulaSettingsSelectProps>
> = ({ filterKey, isDisabled }) => {
    const {
        editorDetails: { formulaType, formula },
    } = useContext(EditorContext);

    const formulaId = formula?.id;

    const history = useHistory<LocationState>();
    const queryClient = useQueryClient();

    const formulaTagsUpdate = useFormulaTagsMutation(formulaId);
    const formulaCompoundTriggerUpdate =
        useFormulaCompoundTriggerMutation(formulaId);
    const messageCreate = useFormulaCreateMutation(formulaType);
    const formulaAudienceUpdate = useFormulaAudienceMutation(formulaId);

    const { data: audiences } = useAudiencesDetailQuery();

    const initialValues = useMemo<FormulaSettingsInitialValueProps>(
        () => ({
            ...getTagsInitialValues(formula?.tags ?? []),
            compound_trigger: formula?.compound_trigger
                ? {
                      label: formula?.compound_trigger?.name,
                      value: formula?.compound_trigger?.id,
                  }
                : null,
            audience_id:
                formula?.audiences?.[0]?.audience_id ?? ALL_USERS_AUDIENCE_ID,
            reward_function_id: formula?.reward_function_id,
            cms_type: formula?.cms_type
                ? {
                      label: CMSDisplayNames[formula?.cms_type],
                      value: formula?.cms_type,
                  }
                : DEFAULT_CMS_TYPE_OPTION,
            cms_filters: formula?.cms_placeholder_filters ?? [],
            cms_filter_type: formula?.cms_placeholder_filters?.length
                ? CMSFilterType.SPECIFIC
                : CMSFilterType.GENERAL,
        }),
        [formula],
    );

    const handleSuccess = useCallback(
        (formulaId: number) => {
            queryClient.invalidateQueries({ queryKey: formulaKeys.all });
            if (history.location.pathname === routes.formulasCreate) {
                history.replace(routes.formulaDraft(formulaId), {
                    from: history.location.state?.from,
                });
            }
        },
        [history, queryClient],
    );

    const handleCreateUpdate = useCallback(
        (
            values: FormulaUpdateModel & {
                cms_filters?: CMSFilterCreateModel[];
            },
            helpers: FormikHelpers<FormulaSettingsInitialValueProps>,
        ) => {
            if (formulaId) {
                if (filterKey === FormulaProperty.Tags) {
                    formulaTagsUpdate.mutate(
                        { tags: values.tags ?? [] },
                        {
                            onSuccess: () => handleSuccess(formulaId),
                            onSettled: () => {
                                helpers.setSubmitting(false);
                            },
                        },
                    );
                } else if (filterKey === FormulaProperty.CompoundTrigger) {
                    formulaCompoundTriggerUpdate.mutate(
                        {
                            compound_trigger_id: values.compound_trigger_id,
                        },
                        {
                            onSuccess: () => handleSuccess(formulaId),
                            onSettled: () => {
                                helpers.setSubmitting(false);
                            },
                        },
                    );
                } else if (filterKey === FormulaProperty.Audience) {
                    formulaAudienceUpdate.mutate(
                        {
                            audience_id:
                                values.audience_id === ALL_USERS_AUDIENCE_ID
                                    ? undefined
                                    : values.audience_id!,
                        },
                        { onSuccess: () => handleSuccess(formulaId) },
                    );
                }
            } else if (formulaType) {
                messageCreate.mutate(values, {
                    onSuccess: (data) => handleSuccess(data.data.id),
                });
            }
        },
        [
            formulaId,
            formulaType,
            filterKey,
            handleSuccess,
            formulaTagsUpdate,
            formulaCompoundTriggerUpdate,
            formulaAudienceUpdate,
            messageCreate,
        ],
    );
    return (
        <Formik
            onSubmit={(
                values: FormulaSettingsInitialValueProps,
                helpers: FormikHelpers<FormulaSettingsInitialValueProps>,
            ) => {
                helpers.setSubmitting(true);
                const updateValues: FormulaUpdateModel & {
                    cms_filters?: CMSFilterCreateModel[];
                } = {};

                if (filterKey === FormulaProperty.Tags) {
                    updateValues[FormulaProperty.Tags] = values?.tags?.map(
                        (tag) => tag.value,
                    );
                } else if (filterKey === FormulaProperty.CompoundTrigger) {
                    updateValues.compound_trigger_id =
                        (values.compound_trigger?.value as string) || undefined;
                } else if (filterKey === FormulaProperty.Audience) {
                    updateValues.audience_id = values.audience_id;
                }

                handleCreateUpdate(updateValues, helpers);
            }}
            initialValues={initialValues}
            enableReinitialize
        >
            {(formikProps) => (
                <StyledForm>
                    {filterKey === FormulaProperty.Tags && (
                        <TagsInput
                            formikProps={formikProps as any}
                            label={'Custom tags'}
                            placeholder="Add custom tags to organise your catalogue"
                            isFormSubmitAllowed
                            isDisabled={isDisabled}
                        />
                    )}
                    {filterKey === FormulaProperty.CompoundTrigger && (
                        <CompoundTriggerInput
                            formikProps={formikProps as any}
                            isFormSubmitAllowed
                            label="Triggers"
                            isDisabled={isDisabled}
                        />
                    )}
                    {filterKey === FormulaProperty.Audience && (
                        <>
                            <AudienceSelect
                                audiences={audiences ?? []}
                                selected={formikProps.values.audience_id!}
                                handleChange={(value: string) => {
                                    formikProps.setFieldValue(
                                        'audience_id',
                                        value,
                                    );
                                    formikProps.handleSubmit();
                                }}
                                label="Audience"
                                isDisabled={isDisabled}
                                showHoverCard
                            />
                        </>
                    )}
                </StyledForm>
            )}
        </Formik>
    );
};

const StyledForm = styled(Form)(() => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
}));

export default FormulaSettingsSelect;
